import Rails from '@rails/ujs';

export default class BackofficeProjectsShow {
  constructor() {
    this.initOrdersDataTable();
    this.initStudyRequestsDataTable();
    this.initBulkImportModal();
    this.foundItemIds = [];
    this.currentImportType = 'orders'; // 'orders' or 'study_requests'
  }

  /**
   * Initialize DataTable for the orders table
   */
  initOrdersDataTable() {
    const table = $('#orders-datatable');
    if (table.length === 0) {
      return;
    }

    this.ordersDataTable = table.DataTable({
      paging: true,
      ordering: true,
      searching: true,
      info: true,
      lengthChange: false,
      pageLength: 10,
      language: {
        url: '/data-tables/locales/' + I18n.locale + '.json'
      },
      columnDefs: [
        {
          targets: [4, 6],
          className: 'text-center'
        },
        {
          targets: 10,
          orderable: false
        }
      ],
      order: [[2, 'desc']] // Order by date descending by default
    });
  }

  /**
   * Initialize DataTable for the study requests table
   */
  initStudyRequestsDataTable() {
    const table = $('#study-requests-datatable');
    if (table.length === 0) {
      return;
    }

    this.studyRequestsDataTable = table.DataTable({
      paging: true,
      ordering: true,
      searching: true,
      info: true,
      lengthChange: false,
      pageLength: 10,
      language: {
        url: '/data-tables/locales/' + I18n.locale + '.json'
      },
      columnDefs: [
        {
          targets: 6,
          orderable: false
        }
      ],
      order: [[2, 'desc']] // Order by date descending by default
    });
  }

  /**
   * Initialize bulk import modal
   */
  initBulkImportModal() {
    const modal = $('#bulk-import-modal');
    if (modal.length === 0) {
      return;
    }

    this.modal = modal;
    this.previewOrdersUrl = modal.data('preview-orders-url');
    this.importOrdersUrl = modal.data('import-orders-url');
    this.previewStudyRequestsUrl = modal.data('preview-study-requests-url');
    this.importStudyRequestsUrl = modal.data('import-study-requests-url');

    // Reset modal when closed
    modal.on('hidden.bs.modal', () => this.resetModal());

    // Track tab changes
    $('a[data-toggle="tab"]').on('shown.bs.tab', (e) => {
      const targetId = $(e.target).attr('href');
      if (targetId === '#import-orders-tab') {
        this.currentImportType = 'orders';
      } else if (targetId === '#import-study-requests-tab') {
        this.currentImportType = 'study_requests';
      }
    });

    // Preview button click
    $('#bulk-import-preview-btn').on('click', () => this.previewImport());

    // Back button click
    $('#bulk-import-back-btn').on('click', () => this.showInputStep());

    // Confirm button click
    $('#bulk-import-confirm-btn').on('click', () => this.confirmImport());
  }

  /**
   * Reset modal to initial state
   */
  resetModal() {
    $('#bulk-import-orders-service-numbers').val('');
    $('#bulk-import-study-requests-service-numbers').val('');
    this.showInputStep();
    this.foundItemIds = [];
    this.currentImportType = 'orders';
    // Reset to orders tab
    $('#import-orders-nav').tab('show');
  }

  /**
   * Show input step (step 1)
   */
  showInputStep() {
    $('#bulk-import-step-input').removeClass('d-none');
    $('#bulk-import-step-review').addClass('d-none');
    $('#bulk-import-loading').addClass('d-none');
    
    $('#bulk-import-preview-btn').removeClass('d-none');
    $('#bulk-import-back-btn').addClass('d-none');
    $('#bulk-import-confirm-btn').addClass('d-none');
  }

  /**
   * Show review step (step 2)
   */
  showReviewStep() {
    $('#bulk-import-step-input').addClass('d-none');
    $('#bulk-import-step-review').removeClass('d-none');
    $('#bulk-import-loading').addClass('d-none');
    
    $('#bulk-import-preview-btn').addClass('d-none');
    $('#bulk-import-back-btn').removeClass('d-none');
    $('#bulk-import-confirm-btn').removeClass('d-none');
  }

  /**
   * Show loading state
   */
  showLoading() {
    $('#bulk-import-step-input').addClass('d-none');
    $('#bulk-import-step-review').addClass('d-none');
    $('#bulk-import-loading').removeClass('d-none');
    
    $('#bulk-import-preview-btn').addClass('d-none');
    $('#bulk-import-back-btn').addClass('d-none');
    $('#bulk-import-confirm-btn').addClass('d-none');
  }

  /**
   * Preview import - call backend to check service numbers
   */
  previewImport() {
    const isOrders = this.currentImportType === 'orders';
    const textareaId = isOrders ? '#bulk-import-orders-service-numbers' : '#bulk-import-study-requests-service-numbers';
    const serviceNumbers = $(textareaId).val().trim();
    const previewUrl = isOrders ? this.previewOrdersUrl : this.previewStudyRequestsUrl;
    const noServiceNumbersKey = isOrders 
      ? 'backoffice.projects.bulk_import_no_service_numbers' 
      : 'backoffice.projects.bulk_import_study_requests_no_service_numbers';
    
    if (!serviceNumbers) {
      application.alertNotification(I18n.t(noServiceNumbersKey));
      return;
    }

    this.showLoading();

    Rails.ajax({
      url: previewUrl,
      type: 'POST',
      data: `service_numbers=${encodeURIComponent(serviceNumbers)}`,
      success: (response) => this.handlePreviewResponse(response),
      error: () => {
        application.alertNotification(I18n.t('backoffice.projects.bulk_import_error'));
        this.showInputStep();
      }
    });
  }

  /**
   * Handle preview response from backend
   */
  handlePreviewResponse(response) {
    const { found, already_in_project, not_found } = response;
    const isOrders = this.currentImportType === 'orders';
    
    // Store found item IDs for confirmation
    this.foundItemIds = found.map(item => item.id);

    // Set review title
    const reviewTitle = isOrders 
      ? I18n.t('backoffice.projects.bulk_import_orders_review')
      : I18n.t('backoffice.projects.bulk_import_study_requests_review');
    $('#bulk-import-review-title').text(reviewTitle);

    // Reset all sections
    $('#bulk-import-found-section').addClass('d-none');
    $('#bulk-import-already-section').addClass('d-none');
    $('#bulk-import-notfound-section').addClass('d-none');
    $('#bulk-import-no-items').addClass('d-none');

    // Found items section
    if (found.length > 0) {
      $('#bulk-import-found-section').removeClass('d-none');
      const foundTitleKey = isOrders 
        ? 'backoffice.projects.bulk_import_orders_found'
        : 'backoffice.projects.bulk_import_study_requests_found';
      $('#bulk-import-found-title').text(I18n.t(foundTitleKey, { count: found.length }));
      
      const foundList = $('#bulk-import-found-list');
      foundList.empty();
      found.forEach(item => {
        foundList.append(`<tr><td>${this.escapeHtml(item.service_number)}</td><td>${this.escapeHtml(item.entity || '--')}</td></tr>`);
      });
    }

    // Already in project section
    if (already_in_project.length > 0) {
      $('#bulk-import-already-section').removeClass('d-none');
      const alreadyTitleKey = isOrders 
        ? 'backoffice.projects.bulk_import_orders_already_in_project'
        : 'backoffice.projects.bulk_import_study_requests_already_in_project';
      $('#bulk-import-already-title').text(I18n.t(alreadyTitleKey, { count: already_in_project.length }));
      
      const alreadyList = $('#bulk-import-already-list');
      alreadyList.empty();
      already_in_project.forEach(item => {
        alreadyList.append(`<li><i class="mdi mdi-minus mr-1"></i>${this.escapeHtml(item.service_number)}</li>`);
      });
    }

    // Not found section
    if (not_found.length > 0) {
      $('#bulk-import-notfound-section').removeClass('d-none');
      const notFoundTitleKey = isOrders 
        ? 'backoffice.projects.bulk_import_orders_not_found'
        : 'backoffice.projects.bulk_import_study_requests_not_found';
      $('#bulk-import-notfound-title').text(I18n.t(notFoundTitleKey, { count: not_found.length }));
      
      const notFoundList = $('#bulk-import-notfound-list');
      notFoundList.empty();
      not_found.forEach(serviceNumber => {
        notFoundList.append(`<li><i class="mdi mdi-minus mr-1"></i>${this.escapeHtml(serviceNumber)}</li>`);
      });
    }

    // Show "no items to import" if nothing found
    if (found.length === 0) {
      $('#bulk-import-no-items').removeClass('d-none');
      const noItemsKey = isOrders 
        ? 'backoffice.projects.bulk_import_no_orders'
        : 'backoffice.projects.bulk_import_no_study_requests';
      $('#bulk-import-no-items-message').text(I18n.t(noItemsKey));
      $('#bulk-import-confirm-btn').addClass('d-none');
    }

    this.showReviewStep();
  }

  /**
   * Confirm import - send item IDs to backend
   */
  confirmImport() {
    if (this.foundItemIds.length === 0) {
      const noItemsKey = this.currentImportType === 'orders' 
        ? 'backoffice.projects.bulk_import_no_orders'
        : 'backoffice.projects.bulk_import_no_study_requests';
      application.alertNotification(I18n.t(noItemsKey));
      return;
    }

    this.showLoading();

    const isOrders = this.currentImportType === 'orders';
    const importUrl = isOrders ? this.importOrdersUrl : this.importStudyRequestsUrl;
    const paramName = isOrders ? 'order_ids[]' : 'study_request_ids[]';
    const data = this.foundItemIds.map(id => `${paramName}=${id}`).join('&');

    Rails.ajax({
      url: importUrl,
      type: 'POST',
      data: data,
      success: (response) => {
        if (response.success) {
          application.successNotification(response.message);
          this.modal.modal('hide');
          // Reload page to show updated list
          window.location.reload();
        } else {
          application.alertNotification(response.error || I18n.t('backoffice.projects.bulk_import_error'));
          this.showReviewStep();
        }
      },
      error: () => {
        application.alertNotification(I18n.t('backoffice.projects.bulk_import_error'));
        this.showReviewStep();
      }
    });
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    if (!text) {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onDestroy() {
    if (this.ordersDataTable) {
      this.ordersDataTable.destroy();
      this.ordersDataTable = null;
    }
    if (this.studyRequestsDataTable) {
      this.studyRequestsDataTable.destroy();
      this.studyRequestsDataTable = null;
    }
  }
}
